import * as React from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import { InfoBar } from "src/library/components/InfoBar";
import { IInfoItem } from "src/library/components/InfoBar/InfoBarItem";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { sentenceCase } from "src/library/utils";
import { IProduct } from "src/core/products/api/products.api";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

import ProductStatus from "./ProductStatus";

interface IProps {
  isLoading: boolean;
  isAdmin?: boolean;
  product: Partial<IProduct> | null;
  primaryAction?: JSX.Element | null;
  secondaryAction?: JSX.Element | null;
  terminology: ITerminology;
  /** The external supplier header mapping based on product import settings */
  supplierDisplayTerm?: string;
}

const ProductInfoSidebar: React.ReactFCWithChildren<IProps> = ({
  product, isLoading, isAdmin, primaryAction, secondaryAction, terminology, supplierDisplayTerm
}) => {
  const checkPermission = useCheckUserPermission();

  const items = React.useMemo(() => {
    const infoBarItems: Array<IInfoItem | null> = [
      {
        label: `${terminology.product.singular.text} Name`,
        value: product ? product.description : "-"
      },
      {
        label: terminology?.partNumber.singular.text || "Part number",
        value: product && product.partNumber ? product.partNumber : "-"
      },
      product?.packSizes && product.packSizes[ 0 ] ? {
        label: "Pack size",
        value: `${product.packSizes[ 0 ].name} of ${product.packSizes[ 0 ].quantity}`
      } as IInfoItem : null,
      {
        label: "Barcode",
        value: product && product.barcode ? product.barcode : "-"
      },
      product?.leadTimeGroup ?
        {
          label: "Lead Time Group",
          value: product ? `${product?.leadTimeGroup.name}` : "-"
        } : null,
      product?.shelfLife ? {
        label: `${terminology.shelfLife ? terminology.shelfLife.singular.text : "Shelf life"}`,
        value: `${product.shelfLife} ${product.shelfLifePeriod || "days"}`
      } as IInfoItem : null,
      {
        label: "Status",
        value: product?.currentStatus?.status ? <ProductStatus status={product.currentStatus.status} /> : "-"
      }
    ];

    if (product && product.customData) {
      for (const customField of product.customData) {
        infoBarItems.push({
          label: customField.description,
          value: customField.value
        });
      }
    }

    // If the user is admin, attach the published status and minimum order quantity
    if (isAdmin) {
      infoBarItems.push(
        {
          label: "Published",
          value: product ? (
            <SideBarPublished published={product.published ? product.published : false}>
              <p>
                {product.published ? "Published" : "Not Published"}
              </p>
            </SideBarPublished>
          ) : (
            "-"
          )
        },
        {
          label: "Minimum Order Quantity",
          value: product?.minimumQuantity ? product.minimumQuantity : "-"
        }
      );

      if (checkPermission("view-supplier")) {
        infoBarItems.push({
          label: supplierDisplayTerm || "Supplier",
          value: product?.supplier?.name || "-"
        });
      }
    }

    return infoBarItems;
  }, [
    isAdmin,
    product,
    terminology?.partNumber.singular.text,
    terminology.product.singular.text,
    terminology.shelfLife,
    supplierDisplayTerm,
    checkPermission
  ]);

  return (
    <InfoBar
      title={`${sentenceCase(terminology.product.singular.text)} Information`}
      isLoading={isLoading}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      items={_.compact(items)}
    />
  );
};

// const SideBarPrice = styled.div`
//   ${({ theme }) => css`
//     font-size: ${theme.fonts.sizes.h2};
//     font-weight: 700px;
//     color: ${theme.colors.primaryDark};
//   `}
// `;

const SideBarPublished = styled.div<{ published: boolean }>`
  ${({ theme, published }) => css`
    p {
      color: ${published ? `${theme.colors.success}` : `${theme.colors.warn}`};
    }
  `}
`;

export default ProductInfoSidebar;
